import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    FormControl,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useAuthContext } from "src/auth/useAuthContext";
import CustomInput from "src/components/customerinput/CustomerInput";
import { notify } from "src/components/notify";
import { VendorContext } from "src/contexts/VendorContext";
import { truncateText } from "src/functions";
// import getEventsData from "src/services/event-slot/getEvents";
import getCreatedQrs from "src/services/generate-qr/getCreatedQrs";
import QRCode from "qrcode";
import { QRCodeCanvas } from "qrcode.react";
import getEventsData from "src/services/generate-qr/getEvents";

export default function GenerateQrPage() {
    const navigate = useNavigate();
    const { vendors, getVendor } = useContext(VendorContext);
    const { user } = useAuthContext();

    const [vendorId, setVendorId] = useState({ _id: "", name: "" });
    const [eventsList, setEventsList] = useState([]);
    const [selectEvent, setSelectEvent] = useState(null);
    const [info, setInfo] = useState(null);

    /* -------------------- LOADING STATES -------------------- */
    const [vendorLoading, setVendorLoading] = useState(false);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [qrFetching, setQrFetching] = useState(false);
    const [qrGenerating, setQrGenerating] = useState(false);
    const [qrDownloading, setQrDownloading] = useState(false);

    /* -------------------- FETCH VENDORS -------------------- */
    useEffect(() => {
        const fetchVendor = async () => {
            try {
                setVendorLoading(true);
                await getVendor(0, true);
            } catch (error) {
                console.error(error);
            } finally {
                setVendorLoading(false);
            }
        };
        fetchVendor();
    }, []);

    /* -------------------- FETCH EVENTS -------------------- */
    useEffect(() => {
        const fetchEvents = async () => {
            try {
                setEventsLoading(true);
                const data = await getEventsData(vendorId._id);
                setEventsList(data);
            } catch (error) {
                console.error(error);
            } finally {
                setEventsLoading(false);
            }
        };

        if (vendorId?._id) {
            fetchEvents();
        } else {
            setEventsList([]);
            setSelectEvent(null);
            setInfo(null);
        }
    }, [vendorId?._id]);

    /* -------------------- FETCH EXISTING QR -------------------- */
    useEffect(() => {
        const fetchQrInfo = async () => {
            try {
                setQrFetching(true);
                const response = await getCreatedQrs(
                    user,
                    "get",
                    undefined,
                    { event: selectEvent, vendor_id: vendorId._id }
                );
                setInfo(response?._payload?.[0] || null);
            } catch (error) {
                console.error(error);
            } finally {
                setQrFetching(false);
            }
        };

        if (selectEvent && vendorId._id) {
            fetchQrInfo();
        } else {
            setInfo(null);
        }
    }, [vendorId._id, selectEvent]);

    /* -------------------- GENERATE QR -------------------- */
    const handleSubmit = async () => {
        if (!vendorId._id) return notify("Partner is required.");
        if (!selectEvent) return notify("Event is required.");

        try {
            setQrGenerating(true);
            const response = await getCreatedQrs(
                user,
                "post",
                { event: selectEvent, vendor_id: vendorId._id }
            );
            if (response) setInfo(response);
        } catch (error) {
            console.error(error);
        } finally {
            setQrGenerating(false);
        }
    };

    /* -------------------- DOWNLOAD QR -------------------- */
    const handleDownloadQRCode = async () => {
        try {
            setQrDownloading(true);
            const qrCodeUrl = await QRCode.toDataURL(info?.wa_qr_link, {
                errorCorrectionLevel: "H",
                width: 400,
            });

            const downloadLink = document.createElement("a");
            downloadLink.href = qrCodeUrl;
            downloadLink.download = vendorId?.name
                ? `${vendorId.name}_qr_code.png`
                : "qr_code.png";
            downloadLink.click();
        } catch (error) {
            console.error("Error generating QR code:", error);
        } finally {
            setQrDownloading(false);
        }
    };

    return (
        <Paper sx={{ p: 2, border: 1, borderColor: "divider" }}>
            <Typography variant="h5" color="primary" mb={2}>
                Generate QR
            </Typography>

            <Stack spacing={2} sx={{ maxWidth: 500 }}>
                {/* -------------------- PARTNER SELECT -------------------- */}
                <CustomInput
                    label="Select Partner"
                    input={
                        <Autocomplete
                            options={vendors}
                            loading={vendorLoading}
                            value={vendorId}
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(o, v) => o._id === v._id}
                            onChange={(_, val) => setVendorId(val || { _id: "", name: "" })}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Partner"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {vendorLoading && (
                                                    <CircularProgress size={18} />
                                                )}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    }
                />

                {/* -------------------- EVENT SELECT -------------------- */}
                <CustomInput
                    label="Event"
                    input={
                        <FormControl fullWidth size="small">
                            <Select
                                value={selectEvent || ""}
                                disabled={eventsLoading || !vendorId._id}
                                onChange={(e) => setSelectEvent(e.target.value)}
                            >
                                {eventsLoading ? (
                                    <MenuItem disabled>
                                        <Stack direction="row" gap={1}>
                                            <CircularProgress size={16} />
                                            <Typography>Loading events...</Typography>
                                        </Stack>
                                    </MenuItem>
                                ) : (
                                    eventsList.map((item) => (
                                        <MenuItem key={item._id} value={item._id}>
                                            <Box display="flex" alignItems="center">
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        mr: 1,
                                                        bgcolor:
                                                            item.event_status === 2
                                                                ? "green"
                                                                : item.event_status === 1
                                                                    ? "orange"
                                                                    : "red",
                                                    }}
                                                />
                                                {truncateText(item.name, 24)}
                                            </Box>
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>
                    }
                />
            </Stack>

            {/* -------------------- QR FETCHING -------------------- */}
            {qrFetching && (
                <Stack direction="row" gap={1} mt={2}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Checking existing QR...</Typography>
                </Stack>
            )}

            {/* -------------------- QR DISPLAY -------------------- */}
            {info && (
                <Paper
                    sx={{
                        mt: 3,
                        p: 2,
                        border: "1px solid lightgrey",
                        display: "inline-flex",
                        flexDirection: "column",
                        gap: 2,
                        alignItems: "center",
                    }}
                >
                    <QRCodeCanvas value={info?.wa_qr_link} size={200} />
                    <Button
                        variant="contained"
                        onClick={handleDownloadQRCode}
                        disabled={qrDownloading}
                    >
                        {qrDownloading ? "Downloading..." : "Download"}
                    </Button>
                </Paper>
            )}

            {/* -------------------- ACTION BUTTONS -------------------- */}
            <Stack direction="row" gap={1} mt={3}>
                {!info && (
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={qrGenerating}
                    >
                        {qrGenerating ? "Generating..." : "Generate"}
                    </Button>
                )}
                <Button variant="outlined" onClick={() => navigate(-1)}>
                    Cancel
                </Button>
            </Stack>
        </Paper>
    );
}
